import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import { useTranslation } from "react-i18next";

const PDFViewer = ({ urls }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const lastPosition = useRef({ x: 0, y: 0 });
  const { t } = useTranslation();

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev * 1.2, 3));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev / 1.2, 0.5));
  };

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  const handleMouseDown = (e) => {
    if (scale === 1) return;
    setIsDragging(true);
    lastPosition.current = {
      x: e.clientX || e.touches[0].clientX,
      y: e.clientY || e.touches[0].clientY,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const currentX = e.clientX || e.touches[0].clientX;
    const currentY = e.clientY || e.touches[0].clientY;

    const deltaX = currentX - lastPosition.current.x;
    const deltaY = currentY - lastPosition.current.y;

    setPosition((prev) => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));

    lastPosition.current = { x: currentX, y: currentY };
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      containerRef.current.style.cursor = "grabbing";
    } else {
      containerRef.current.style.cursor = scale > 1 ? "grab" : "default";
    }
  }, [isDragging, scale]);

  if (!urls || urls.length === 0) {
    return (
      <Box className="flex items-center justify-center h-full">
        <Typography color="textSecondary">
          {t("carnivals.noCvAvailableForCandidate")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        height: "100vh",
        width: "100%",
        overflow: "auto",
        position: "relative",
        touchAction: "none",
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        scrollbarWidth: 'none', 
        msOverflowStyle: 'none' 
      }}
      
      onScroll={(e) => {
        if (scale !== 1) e.preventDefault();
      }}
    >
      {/* Zoom Controls */}
      <Box
        sx={{
          position: "sticky",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 1,
          bgcolor: "background.paper",
          borderRadius: 4,
          p: 1,
          boxShadow: 3,
          zIndex: 1000,
          margin: "0 auto",
          width: "fit-content",
        }}
      >
        <IconButton onClick={handleZoomOut} disabled={scale <= 0.5}>
          <ZoomOutIcon />
        </IconButton>
        <IconButton onClick={handleReset}>
          <ZoomOutMapIcon />
        </IconButton>
        <IconButton onClick={handleZoomIn} disabled={scale >= 3}>
          <ZoomInIcon />
        </IconButton>
      </Box>

      {/* Scrollable content container */}
      <Box
        ref={contentRef}
        sx={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          transformOrigin: "0 0",
          transition: scale === 1 ? "transform 0.3s" : "none",
          willChange: "transform",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          p: 2,
          minHeight: `calc(100% / ${scale})`, // Maintain proper scroll height
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchMove={handleMouseMove}
        onTouchEnd={handleMouseUp}
      >
        {urls.map((base64Data, index) => (
          <img
            key={index}
            src={`data:image/png;base64,${base64Data}`}
            alt={`Page ${index + 1}`}
            style={{
              maxWidth: "100%",
              width: "auto",
              maxHeight: "90vh",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              pointerEvents: "none",
              marginBottom: index === urls.length - 1 ? 0 : "32px",
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default PDFViewer;